<template>
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Document Access Requests</h1>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">Review and approve requests for access-gated documents.</p>

    <!-- Settings Panel (Admin only) -->
    <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 mb-6">
      <h2 class="text-sm font-semibold text-gray-900 dark:text-white mb-4">Access Settings</h2>
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-approve requests</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">Automatically grant access when someone requests a document</p>
          </div>
          <button type="button" @click="toggleSetting('auto_approve_doc_access')"
            :disabled="settingsLoading"
            class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
            :class="settings.auto_approve_doc_access ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'">
            <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
              :class="settings.auto_approve_doc_access ? 'translate-x-6' : 'translate-x-1'"></span>
          </button>
        </div>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Allow "Request All" on public portal</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">Let visitors request access to all gated documents at once</p>
          </div>
          <button type="button" @click="toggleSetting('allow_bulk_doc_access')"
            :disabled="settingsLoading"
            class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
            :class="settings.allow_bulk_doc_access ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'">
            <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
              :class="settings.allow_bulk_doc_access ? 'translate-x-6' : 'translate-x-1'"></span>
          </button>
        </div>
      </div>
    </div>

    <!-- Action bar -->
    <div v-if="pendingCount > 0" class="flex items-center justify-between mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-4 py-3">
      <p class="text-sm text-amber-800 dark:text-amber-300">
        <span class="font-semibold">{{ pendingCount }}</span> pending request{{ pendingCount !== 1 ? 's' : '' }}
      </p>
      <button @click="approveAll" :disabled="approveAllLoading"
        class="px-4 py-1.5 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors">
        {{ approveAllLoading ? 'Approving...' : 'Approve All Pending' }}
      </button>
    </div>

    <!-- Filter bar -->
    <div class="flex flex-col sm:flex-row gap-2 mb-4">
      <input
        v-model="searchQuery"
        @input="onFilterChange"
        type="search"
        placeholder="Search by name, email, or company..."
        class="input flex-1"
      />
      <select v-model="statusFilter" @change="onFilterChange" class="input sm:w-48">
        <option value="">All statuses</option>
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="denied">Denied</option>
      </select>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-16">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500"></div>
    </div>

    <div v-else-if="requests.length === 0" class="text-center py-16 text-gray-500 dark:text-gray-400">
      <template v-if="searchQuery || statusFilter">
        No requests match your filters.
        <button type="button" @click="clearFilters" class="ml-2 text-blue-600 dark:text-blue-400 hover:underline">Clear filters</button>
      </template>
      <template v-else>No access requests yet.</template>
    </div>

    <div v-else class="space-y-3">
      <div v-for="req in requests" :key="req.id"
        class="card px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div class="flex items-center gap-4 min-w-0 flex-1">
          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0"
            :class="statusStyles(req.status)">
            {{ req.status }}
          </span>
          <div class="min-w-0">
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ req.requester_name }} &lt;{{ req.requester_email }}&gt;</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ req.document_title || `Document #${req.document_id}` }}
              <span v-if="req.company"> &middot; {{ req.company }}</span>
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <span class="text-xs text-gray-400 dark:text-gray-500 mr-2">{{ formatDate(req.created_at) }}</span>
          <template v-if="req.status === 'pending'">
            <button @click="approveRequest(req)" :disabled="actionLoading === req.id"
              class="px-3 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors">
              {{ actionLoading === req.id ? '...' : 'Approve' }}
            </button>
            <button @click="denyRequest(req)" :disabled="actionLoading === req.id"
              class="px-3 py-1.5 text-xs font-medium rounded-md text-red-700 dark:text-red-300 border border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors">
              Deny
            </button>
          </template>
          <span v-else-if="req.status === 'approved' && req.expires_at" class="text-xs text-green-600 dark:text-green-400">
            Expires {{ formatDate(req.expires_at) }}
          </span>
          <span v-else-if="req.status === 'denied'" class="text-xs text-red-500 dark:text-red-400">Denied</span>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-center gap-2 mt-6">
      <button @click="fetchRequests(currentPage - 1)" :disabled="currentPage <= 1"
        class="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">Prev</button>
      <span class="text-sm text-gray-500 dark:text-gray-400">{{ currentPage }} / {{ totalPages }}</span>
      <button @click="fetchRequests(currentPage + 1)" :disabled="currentPage >= totalPages"
        class="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">Next</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import axios from 'axios'

const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const cleanUrl = envUrl.replace(/\/$/, '')
  return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
}

export default {
  name: 'DocumentAccessView',
  setup() {
    const store = useStore()
    const route = useRoute()
    const loading = ref(false)
    const actionLoading = ref(null)
    const approveAllLoading = ref(false)
    const settingsLoading = ref(false)
    const requests = ref([])
    const currentPage = ref(1)
    const totalPages = ref(1)
    const tenantId = computed(() => route.params.tenantId || store.getters.currentTenantId)
    const headers = computed(() => ({ Authorization: `Bearer ${store.state.token}` }))

    const settings = ref({
      auto_approve_doc_access: false,
      allow_bulk_doc_access: true
    })

    const pendingCount = computed(() => requests.value.filter(r => r.status === 'pending').length)

    const searchQuery = ref('')
    const statusFilter = ref('')
    let searchDebounce = null
    const onFilterChange = () => {
      // Debounce so we don't hammer the API on every keystroke.
      clearTimeout(searchDebounce)
      searchDebounce = setTimeout(() => fetchRequests(1), 300)
    }
    const clearFilters = () => {
      searchQuery.value = ''
      statusFilter.value = ''
      clearTimeout(searchDebounce)
      fetchRequests(1)
    }

    const fetchSettings = async () => {
      if (!tenantId.value) return
      try {
        const res = await axios.get(`${getAPIURL()}/tenants/${tenantId.value}/document-settings`)
        settings.value = res.data
      } catch (e) {
        console.error('Failed to fetch doc settings:', e)
      }
    }

    const toggleSetting = async (key) => {
      settingsLoading.value = true
      const prev = settings.value[key]
      settings.value[key] = !prev
      try {
        await axios.put(`${getAPIURL()}/admin/tenants/${tenantId.value}/document-settings`,
          { [key]: settings.value[key] },
          { headers: headers.value }
        )
        store.dispatch('notification/showNotification', { type: 'success', message: 'Setting updated', duration: 2000 })
      } catch (e) {
        settings.value[key] = prev
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to update setting' })
      } finally {
        settingsLoading.value = false
      }
    }

    const fetchRequests = async (page = 1) => {
      if (!tenantId.value) return
      loading.value = true
      try {
        const params = { page, per_page: 20, order_by: 'created_at', sort_direction: 'desc' }
        if (searchQuery.value.trim()) params.search = searchQuery.value.trim()
        if (statusFilter.value) params.status = statusFilter.value
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/document-access-requests`, {
          headers: headers.value,
          params
        })
        const data = res.data
        requests.value = data?.data || data || []
        currentPage.value = data?.currentPage || page
        totalPages.value = data?.totalPages || 1
      } catch (e) {
        console.error('Failed to fetch access requests:', e)
      } finally {
        loading.value = false
      }
    }

    const approveRequest = async (req) => {
      actionLoading.value = req.id
      try {
        await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/document-access-requests/${req.id}/approve`, {}, {
          headers: headers.value
        })
        store.dispatch('notification/showNotification', { type: 'success', message: 'Access approved — email sent to requester' })
        await fetchRequests(currentPage.value)
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: e.response?.data?.error || 'Failed to approve' })
      } finally {
        actionLoading.value = null
      }
    }

    const approveAll = async () => {
      approveAllLoading.value = true
      try {
        const res = await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/document-access-requests/approve-all`, {}, {
          headers: headers.value
        })
        const count = res.data?.count || 0
        store.dispatch('notification/showNotification', { type: 'success', message: `Approved ${count} request${count !== 1 ? 's' : ''} — emails sent` })
        await fetchRequests(currentPage.value)
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: e.response?.data?.error || 'Failed to approve all' })
      } finally {
        approveAllLoading.value = false
      }
    }

    const denyRequest = async (req) => {
      actionLoading.value = req.id
      try {
        await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/document-access-requests/${req.id}/deny`, {}, {
          headers: headers.value
        })
        store.dispatch('notification/showNotification', { type: 'success', message: 'Access denied' })
        await fetchRequests(currentPage.value)
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: e.response?.data?.error || 'Failed to deny' })
      } finally {
        actionLoading.value = null
      }
    }

    const statusStyles = (status) => ({
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': status === 'pending',
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': status === 'approved',
      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': status === 'denied',
    })

    const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''

    onMounted(() => {
      fetchSettings()
      fetchRequests()
    })

    return {
      loading, actionLoading, approveAllLoading, settingsLoading,
      requests, currentPage, totalPages, pendingCount,
      settings, fetchSettings, toggleSetting,
      fetchRequests, approveRequest, approveAll, denyRequest,
      statusStyles, formatDate,
      searchQuery, statusFilter, onFilterChange, clearFilters
    }
  }
}
</script>
